import React from "react";
import {
  DataTableV2DateTypes,
  IDataTableV2DatePlugin,
  DateFilterByType,
} from "./DataTableV2Date.types";
import {
  SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
  IconButton,
  InputAdornment,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import {
  SwapVert,
  Update,
  AddCircleOutline,
} from "@mui/icons-material";
import { getDataTablev2InitialDate } from "../../helpers/dataTableV2DatesFilter";
import { CustomDatePicker } from "../../../formsComponents/CustomDatePicker";
import { ControlledCustomSelect } from "../../../formsComponents";
import { DateTypes } from "../../../Table/hooks/useDateFilter";

export const dateFilterByList: {
  label: string;
  value: DateFilterByType;
  icon: React.ReactNode;
}[] = [
  {
    label: "Updated Date",
    value: "insert_ts",
    icon: <Update fontSize="small" />,
  },
  {
    label: "Created Date",
    value: "create_ts",
    icon: <AddCircleOutline fontSize="small" />,
  },
];

export const dateDropdownList = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last Week", value: "lastWeek" },
  { label: "This Week", value: "thisWeek" },
  { label: "This Month", value: "thisMonth" },
  { label: "Last 7 Days", value: "last7Days" },
  { label: "Last 30 Days", value: "last30Days" },
  { label: "Last 90 Days", value: "last90Days" },
  { label: "All Times", value: "allTimes" },
  { label: "Customize", value: "custom" },
];

const customDatePickerSx = {
  minWidth: {
    md: "140px",
    lg: "160px",
    xl: "180px",
  },
};

export const upcomingBookingDateDropdownList = [
  { label: "Today", value: "today" },
  { label: "Tomorrow", value: "tomorrow" },
  { label: "This Week", value: "upcomingThisWeek" },
  { label: "This Month", value: "upcomingThisMonth" },
  { label: "Next Week", value: "upcomingWeek" },
  { label: "Next Month", value: "upcomingMonth" },
  { label: "Next 3 Months", value: "upcoming3Months" },
  { label: "Next 30 Days", value: "upcoming30Days" },
  { label: "All Times", value: "allTimes" },
  { label: "Customize", value: "upcomingCustom" },
];

export const DataTableV2Date: React.FC<IDataTableV2DatePlugin> = (props) => {
  const {
    state,
    onChange = () => {},
    isFromUpcomingBooking = false,
    isHighlighted = false,
    showFilterByToggle = false,
    sx
  } = props;

  const [filterByAnchorEl, setFilterByAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const filterByOpen = Boolean(filterByAnchorEl);
  const filterByValue: DateFilterByType = state.filterBy ?? "insert_ts";
  // A non-default choice (Updated Date) counts as an active filter.
  const isFilterByActive = filterByValue !== "insert_ts";

  const handleFilterBySelect = (value: DateFilterByType) => {
    onChange({ ...state, filterBy: value });
    setFilterByAnchorEl(null);
  };

  const handleDateRanngeSelectChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as DataTableV2DateTypes;
    if (value === "custom") {
      onChange({
        rangeType: value,
        dates: {
          fromDate: "",
          toDate: "",
        },
      });
      return;
    }
    const dates = getDataTablev2InitialDate(value);
    const newSate = {
      rangeType: value,
      dates: {
        fromDate: dates.fromDate,
        toDate: dates.toDate,
      },
    };
    onChange(newSate);
  };

  return (
    <Stack direction={"row"} spacing={1}>
      <DateFilterDropdown
        value={state.rangeType}
        options={
          isFromUpcomingBooking
            ? upcomingBookingDateDropdownList
            : dateDropdownList
        }
        // sx={sx}
        onChange={handleDateRanngeSelectChange}
        isHighlighted={isHighlighted || isFilterByActive}
        startAdornment={
          showFilterByToggle ? (
            <Tooltip
              title={`Filtering by ${
                filterByValue === "insert_ts" ? "Updated Date" : "Created Date"
              }`}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterByAnchorEl(e.currentTarget);
                }}
                // Prevent the Select from opening when the icon is clicked.
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  // Compact, self-contained button look.
                  p: 0.25,
                  ml: -1.25,
                  mr: 0,
                  // 1px gap below the label line.
                  // mt: "1px",
                  borderRadius: 1,
                  color: "#262626",
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  backgroundColor: "#f5f5f5",
                  // Sit above the floating label so it stays clickable/on top.
                  position: "relative",
                  zIndex: 2,
                  "&:hover": { backgroundColor: "#ebebeb" },
                }}
              >
                <SwapVert fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      />

      {showFilterByToggle && (
        <Popover
          open={filterByOpen}
          anchorEl={filterByAnchorEl}
          onClose={() => setFilterByAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              minWidth: 190,
              mt: 0.5,
              borderRadius: 2,
              boxShadow: "0px 6px 20px rgba(0,0,0,0.12)",
            },
          }}
        >
          <List component="nav" disablePadding sx={{ p: 0.75 }}>
            {dateFilterByList.map((item) => {
              const selected = filterByValue === item.value;
              return (
                <ListItemButton
                  key={item.value}
                  onClick={() => handleFilterBySelect(item.value)}
                  disableRipple
                  sx={{
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.75,
                    mb: 0.25,
                    "&:last-of-type": { mb: 0 },
                    color: selected ? "error.main" : "text.primary",
                    backgroundColor: selected ? "#fdecec" : "transparent",
                    "&:hover": {
                      backgroundColor: selected ? "#fdecec" : "action.hover",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 1.25,
                      color: selected ? "error.main" : "text.secondary",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.85rem",
                      fontWeight: selected ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Popover>
      )}

      {state.rangeType === "custom" && (
        <>
          <CustomDatePicker
            placeholder="From Date"
            sx={sx ?? customDatePickerSx}
            value={state.dates.fromDate}
            onChange={(newValue) => {
              onChange({
                ...state,
                dates: {
                  ...state.dates,
                  fromDate: newValue,
                },
              });
            }}
          />
          <CustomDatePicker
            placeholder="To Date"
            sx={sx ?? customDatePickerSx}
            value={state.dates.toDate}
            onChange={(newValue) => {
              onChange({
                ...state,
                dates: {
                  ...state.dates,
                  toDate: newValue,
                },
              });
            }}
          />
        </>
      )}
    </Stack>
  );
};

export interface IDateFilterDropdownProps {
  value: string | null;
  sx?: SxProps<Theme>;
  excludeOptions?: DateTypes[];
  options: (
    | { label: string; value: string }
    | {
        label: string;
        value: string | number;
        fromDate: string;
        toDate: string;
      }
  )[];
  placeholder?: string;
  onChange?: (e: SelectChangeEvent<unknown>) => void;
  isHighlighted?: boolean;
  startAdornment?: React.ReactNode;
}

const DateFilterDropdown: React.FC<IDateFilterDropdownProps> = (props) => {
  const {
    value,
    options,
    sx,
    onChange,
    excludeOptions,
    placeholder,
    isHighlighted = false,
    startAdornment,
  } = props;

  return (
    <ControlledCustomSelect
      label="Select Duration"
      highlight={isHighlighted}
      sx={[
        { minWidth: { md: "80px", lg: "100px", xl: "120px" } },
        // When the start-adornment button is present, remove the input's
        // default left padding so the button sticks to the left edge (the
        // 4px gap comes from the adornment's own margin).
        startAdornment
          ? {
              "& .MuiInputBase-root": { paddingLeft: 0 },
              // The adornment carries its own small gap; drop its extra margin.
              "& .MuiInputAdornment-root": { marginLeft: 0, marginRight: 0 },
            }
          : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      // InputLabelProps={
      //   isHighlighted
      //     ? {
      //         // Fill the floating label chip with the same highlight colour
      //         // (the shrink label has its own white bg by default).
      //         sx: {
      //           "&.MuiInputLabel-shrink": { backgroundColor: "#fff2c7" },
      //         },
      //       }
      //     : undefined
      // }
      value={value}
      placeholder={placeholder}
      displayEmpty
      onChange={onChange}
      startAdornment={
        startAdornment ? (
          <InputAdornment
            position="start"
            sx={{
              ml: 0,
              mr: 0,
              pr: "6px",
              // Vertical separator between the button and the selected value.
              borderRight: "1px solid rgba(0, 0, 0, 0.23)",
              height: "22px",
              maxHeight: "none",
            }}
          >
            {startAdornment}
          </InputAdornment>
        ) : undefined
      }
      options={options
        .filter(
          (x) => !(excludeOptions && excludeOptions.includes(x.value as any))
        )
        .map((option) => {
          return { label: option.label, value: option.value };
        })}
    ></ControlledCustomSelect>
  );
};

// const [open, setOpen] = React.useState(false);
// const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
// const canBeOpen = open && Boolean(anchorEl);

// const id = canBeOpen ? 'data-table-v2-date-transition-popper' : undefined;

//   const handleClick = (event: React.MouseEvent<HTMLElement>) => {
//     setAnchorEl(event.currentTarget);
//     setOpen((previousOpen) => !previousOpen);
//   };

// <Button variant="text" aria-describedby={id} onClick={handleClick}>
//         <Stack direction={"row"} spacing={1} alignItems={"center"}>
//           <CalendarMonth color="primary" sx={{ fontSize: 25 }} />
//           <Typography variant="h4" fontWeight={600} color="primary.main">
//             Date Filter
//           </Typography>
//         </Stack>
//       </Button>
//       <Popper id={id} open={open} anchorEl={anchorEl}  sx={{ zIndex: 1200 }} transition placeholder={"bottom"}>
//         {({ TransitionProps }) => (

//           <Fade {...TransitionProps} timeout={350}>
//             <Paper>
//               <Typography sx={{ p: 2 }}>The content of the Popper.</Typography>
//             </Paper>
//           </Fade>
//         )}
//       </Popper>
